import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Crown, Shield, Moon, Sun, MapPin, Cake, Target, Sparkles, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";

export default function Profile() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="max-w-xl mx-auto">
      <div className="glass rounded-3xl p-7 text-center mb-5 relative overflow-hidden">
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 h-40 w-40 rounded-full bg-indigo-600/20 blur-3xl" />
        <div className="relative">
          {user?.picture ? (
            <img src={user.picture} alt="" className="h-20 w-20 rounded-full mx-auto mb-3 border-2 border-indigo-500/30 object-cover" />
          ) : (
            <div className="h-20 w-20 rounded-full mx-auto mb-3 bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center font-display font-extrabold text-3xl text-white">{user?.name?.[0]?.toUpperCase() || "P"}</div>
          )}
          <h1 className="font-display font-extrabold text-2xl tracking-tight">{user?.name}</h1>
          <p className="text-muted-foreground text-sm">{user?.email || (user?.is_guest ? "Guest account" : "")}</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/25">Level {user?.level}</span>
            {user?.is_premium && <span className="text-xs font-bold px-3 py-1 rounded-full bg-yellow-500/10 text-yellow-300 border border-yellow-500/25 flex items-center gap-1"><Crown size={12} /> Premium</span>}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[{ l: "Life XP", v: user?.xp ?? 0, c: "text-yellow-400" }, { l: "Coins", v: user?.coins ?? 0, c: "text-emerald-400" }, { l: "Streak", v: `${user?.streak ?? 0}d`, c: "text-orange-400" }].map((s) => (
          <div key={s.l} className="glass rounded-2xl p-4 text-center"><div className={`text-2xl font-display font-extrabold ${s.c}`}>{s.v}</div><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-bold mt-1">{s.l}</div></div>
        ))}
      </div>

      {/* Info */}
      <div className="glass rounded-3xl p-5 mb-5 space-y-3">
        {user?.age && <InfoRow icon={Cake} label="Age" value={user.age} />}
        {user?.city && <InfoRow icon={MapPin} label="City" value={user.city} />}
        {user?.goals?.length > 0 && (
          <div className="flex items-start gap-3">
            <Target size={17} className="text-indigo-400 mt-0.5 shrink-0" />
            <div><span className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Goals</span><div className="flex flex-wrap gap-1.5 mt-1.5">{user.goals.map((g) => <span key={g} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{g}</span>)}</div></div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="space-y-2.5">
        {!user?.is_premium && (
          <ActionRow testid="profile-premium" onClick={() => navigate("/app/premium")} icon={Crown} label="Upgrade to Premium" c="text-yellow-400" />
        )}
        <button data-testid="profile-theme" onClick={toggleTheme} className="w-full flex items-center gap-3 glass rounded-2xl p-4 hover:bg-white/5 transition-colors">
          {theme === "dark" ? <Moon size={19} className="text-violet-400" /> : <Sun size={19} className="text-amber-400" />}
          <span className="flex-1 text-left font-semibold text-sm">Theme: {theme === "dark" ? "Dark" : "Light"}</span>
          <span className="text-xs text-muted-foreground">Tap to switch</span>
        </button>
        {user?.is_admin && (
          <ActionRow testid="profile-admin" onClick={() => navigate("/app/admin")} icon={Shield} label="Admin Dashboard" c="text-cyan-400" />
        )}
        <button data-testid="logout-btn" onClick={doLogout} className="w-full flex items-center gap-3 glass rounded-2xl p-4 hover:bg-rose-500/10 transition-colors text-rose-400">
          <LogOut size={19} /><span className="flex-1 text-left font-semibold text-sm">Log Out</span>
        </button>
      </div>
      <p className="text-center text-[11px] text-muted-foreground mt-6">LyfePilot · Learn the stuff school skipped.</p>
    </div>
  );
}

const InfoRow = ({ icon: I, label, value }) => (
  <div className="flex items-center gap-3"><I size={17} className="text-indigo-400 shrink-0" /><span className="text-xs text-muted-foreground uppercase tracking-wider font-bold w-14">{label}</span><span className="text-sm font-medium">{value}</span></div>
);

const ActionRow = ({ testid, onClick, icon: I, label, c }) => (
  <button data-testid={testid} onClick={onClick} className="w-full flex items-center gap-3 glass rounded-2xl p-4 hover:bg-white/5 transition-colors">
    <I size={19} className={c} /><span className="flex-1 text-left font-semibold text-sm">{label}</span><ChevronRight size={17} className="text-muted-foreground" />
  </button>
);
